import React from 'react';
import type { ComponentEventHandler } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import * as S from './Delivery.styles';
import { useLoaderData } from '@remix-run/react';
import { useVariantShipping } from '../../hook/use-variant-shipping';
import { useVariantShippingMethods } from '@modules/ecommerce/context/VariantShippingMethodsContext';
import type { Locality } from '~/modules/ecommerce/type/locality';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { AngleDown, ChevronDown } from '@archipro-design/icons';
import type { FreeThresholdData } from '~/modules/ecommerce/component/shipping-popup/DeliverySelect';

/** Shared styling for all grey info lines in the stock & shipping section. */
const INFO_LINE = 'text-base text-[#9B9B9B] leading-normal tracking-[-0.02em]';

// ...on ShoppingCartSummary
type DeliveryData = FreeThresholdData & {
    Region?: {
        ID: number;
        Title: string;
    } | null;
    Locality?: Locality | null;
};

interface DeliveryProps {
    isSample: boolean;
    onRegionClick: ComponentEventHandler<void>;
    hasLeadTime?: boolean;
}

const Delivery = ({
    isSample,
    onRegionClick,
    hasLeadTime = false,
}: DeliveryProps) => {
    const { css } = useStyles();
    const { shoppingCartSummary } = useLoaderData<typeof loader>();
    const { shipping, shippingMethods } = useVariantShipping(isSample);
    const { isLoading } = useVariantShippingMethods();
    const { mobile } = useAppDisplayModeContext();

    const cart = shoppingCartSummary as DeliveryData;
    const region = cart.Region?.Title ?? null;
    const shippingMethod =
        shipping?.type === 'delivery'
            ? shippingMethods.find((m) => m.ID === shipping.ID)
            : undefined;

    const cost = shippingMethod?.ShippingCostText ?? '';
    const costRef = React.useRef<HTMLSpanElement>(null);
    const [costWidth, setCostWidth] = React.useState(0);

    React.useEffect(() => {
        if (!shippingMethod || isLoading || !costRef.current) {
            return;
        }

        setCostWidth(costRef.current.offsetWidth);
    }, [shippingMethod, isLoading, cost]);

    if (!region || !shippingMethod) {
        return null;
    }

    const freeShippingThresholdEnabled =
        cart.FreeShippingThreshold && shippingMethod.FreeThresholdEnabled;

    const quotedSeparately = shippingMethod.QuotedSeparately;

    /** Inline shimmer skeleton that matches the width of the cost text. */
    const costSkeleton = (
        <span
            className="relative inline-block h-4 overflow-hidden rounded bg-gray-50 align-middle after:absolute after:inset-0 after:-translate-x-full after:animate-shimmer after:bg-gradient-to-r after:from-transparent after:via-white/60 after:to-transparent"
            style={{ width: costWidth > 0 ? costWidth : 96 }}
        />
    );

    const costText =
        freeShippingThresholdEnabled && cart.FreeShippingThresholdMet ? (
            <>
                (<span className={css(S.MetFreeThreshold)}>{cost}</span> Free)
            </>
        ) : (
            cost
        );

    const displayCost = isLoading ? (
        costSkeleton
    ) : (
        <span ref={costRef}>{costText}</span>
    );

    const deliveryText = shippingMethod.DeliveryEstimateText;

    return shippingMethod ? (
        <>
            <div className={INFO_LINE}>
                {shippingMethod.Title} {!quotedSeparately && displayCost} to{' '}
                <u className={css(S.Region)} onClick={onRegionClick}>
                    {region}
                    {!mobile && (
                        <ChevronDown className={css(S.DeliverySelectIcon)} />
                    )}
                    {mobile && (
                        <span className={css(S.Icon)}>
                            <AngleDown />
                        </span>
                    )}
                </u>
            </div>
            {deliveryText && (
                <div className={INFO_LINE}>
                    {deliveryText}
                    {hasLeadTime ? ', after lead time' : null}
                </div>
            )}
            {freeShippingThresholdEnabled && !cart.FreeShippingThresholdMet && (
                <div className={INFO_LINE}>
                    Free Shipping for orders over{' '}
                    {cart.FreeShippingThreshold?.ShortFormat}
                </div>
            )}
        </>
    ) : null;
};

export default Delivery;
